import BusinessProviderAuth from '@/components/business/BusinessProviderAuth';
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import useSeo from "@/hooks/useSeo";

import { businessRequest, TOKEN_KEY } from "@/lib/businessApi";

export default function BusinessLogin() {
  useSeo({ title: "Business Login — Orrbbit", description: "Log in to your Orrbbit Business account to manage your profile and events.", path: "/business/login" });
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const addingBusiness = params.get("setup") === "1";
  const [emailOnly, setEmailOnly] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setMsg("");
    try {
      let data = await businessRequest('/auth/login', { method: 'POST', token: null, body: { email: email.trim(), password } });
      data = await businessRequest('/auth/persona', { method: 'POST', token: data.access_token, body: { persona: 'business', add_business: addingBusiness } });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      navigate('/business/dashboard');
    } catch (error) {
      setMsg(error.status === 429 ? 'Too many attempts. Please try again in a few minutes.' : error.message || 'Unable to log in. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="business-login-page">
      <section className="container-x section-space">
        <Reveal className="mx-auto max-w-md rounded-3xl border border-cobalt/20 bg-white p-8 shadow-card">
          <div className="mx-auto flex max-w-[220px] flex-col items-end"><img src="/brand/orrbbit-logo.png" alt="Orrbbit" className="h-auto w-full"/><span className="text-xl font-bold text-cobalt">Business</span></div>
          <h1 className="mt-5 text-center font-heading text-2xl font-bold tracking-tight text-navy">Welcome back</h1>
          <p className="mt-2 text-center text-sm text-bodygrey">Sign in to manage your Business on Orrbbit.</p>
          <BusinessProviderAuth mode="signin" onEmail={() => setEmailOnly(true)} />
          {emailOnly && <form className="mt-6 space-y-4" onSubmit={submit}>
            <div>
              <label htmlFor="business-login-email" className="text-xs font-semibold uppercase tracking-wide text-bodygrey">Email</label>
              <input id="business-login-email" data-testid="business-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full rounded-xl border border-border bg-white px-4 py-3 text-sm text-navy outline-none focus:border-cobalt focus:ring-2 focus:ring-cobalt/20" placeholder="you@business.com" />
            </div>
            <div>
              <label htmlFor="business-login-password" className="text-xs font-semibold uppercase tracking-wide text-bodygrey">Password</label>
              <input id="business-login-password" data-testid="business-login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full rounded-xl border border-border bg-white px-4 py-3 text-sm text-navy outline-none focus:border-cobalt focus:ring-2 focus:ring-cobalt/20" placeholder="••••••••" />
              <div className="mt-1.5 text-right">
                <Link to="/business/forgot-password" data-testid="business-forgot-password-link" className="text-xs font-semibold text-cobalt hover:underline">Forgot password?</Link>
              </div>
            </div>
            {msg && <p data-testid="business-login-msg" className="rounded-xl bg-orange/5 px-3 py-2 text-sm text-orange">{msg}</p>}
            <button data-testid="business-login-submit" type="submit" disabled={loading}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-cobalt px-6 py-3 font-semibold text-white transition-transform hover:scale-[1.02] disabled:opacity-60">
              {loading ? <Loader2 size={18} className="animate-spin" /> : <>Log In <ArrowRight size={16} /></>}
            </button>
          </form>}
        </Reveal>
      </section>
    </div>
  );
}

